import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';
import { cn } from '@/lib/cn';
import { LanguageSwitcher } from './LanguageSwitcher';
import { Logo } from './Logo';

const links = [
  { to: '/lavori', key: 'nav.work' },
  { to: '/design', key: 'nav.design' },
  { to: '/studio', key: 'nav.studio' },
  { to: '/contatti', key: 'nav.contact' },
] as const;

export function Navbar() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const onHome = pathname === '/';
  // White text only when sitting over the home hero photo; ink everywhere else.
  const overHero = onHome && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close the mobile menu whenever the route changes.
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <>
      <header
        className={cn(
          'fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,padding] duration-500',
          scrolled
            ? 'border-b border-line bg-paper/85 py-3 backdrop-blur-md'
            : 'border-b border-transparent py-5',
        )}
      >
        <nav
          className={cn(
            'u-container flex items-center justify-between transition-colors duration-300',
            overHero ? 'text-white' : 'text-ink',
          )}
        >
          <Link to="/" aria-label="Silvana Barbato — Studio Atelier, home">
            <Logo className="h-10 md:h-12" />
          </Link>

          <div className="hidden items-center gap-9 md:flex">
            <ul className="flex items-center gap-9 text-sm uppercase tracking-[0.16em]">
              {links.map((l) => {
                const active = pathname === l.to;
                return (
                  <li key={l.to}>
                    <Link
                      to={l.to}
                      aria-current={active ? 'page' : undefined}
                      className={cn(
                        'relative inline-block py-1 transition-opacity',
                        active ? 'opacity-100' : 'opacity-70 hover:opacity-100 link-line',
                      )}
                    >
                      {t(l.key)}
                      {active && (
                        <span aria-hidden className="absolute -bottom-0.5 left-0 h-[2px] w-full bg-brass" />
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
            <LanguageSwitcher />
          </div>

          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center md:hidden"
            aria-label={open ? t('nav.close') : t('nav.menu')}
            aria-expanded={open}
          >
            <span className="relative block h-3 w-6">
              <span className={cn('absolute left-0 h-px w-6 bg-current transition-all duration-300', open ? 'top-1.5 rotate-45' : 'top-0')} />
              <span className={cn('absolute left-0 top-1.5 h-px w-6 bg-current transition-opacity duration-300', open && 'opacity-0')} />
              <span className={cn('absolute left-0 h-px w-6 bg-current transition-all duration-300', open ? 'top-1.5 -rotate-45' : 'top-3')} />
            </span>
          </button>
        </nav>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-40 flex flex-col justify-center bg-ink px-8 text-paper md:hidden"
          >
            <ul className="space-y-6">
              {links.map((l, i) => (
                <motion.li
                  key={l.to}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.06, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                >
                  <Link to={l.to} className="font-display text-4xl">
                    {t(l.key)}
                  </Link>
                </motion.li>
              ))}
            </ul>
            <div className="mt-12">
              <LanguageSwitcher />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
